import React, { useState, useRef } from 'react';
import { photoService } from '../../services/api';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';
import './PhotoUpload.css';

const PhotoUpload = ({ onClose, onUploaded, albums = [], defaultAlbum }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(defaultAlbum || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFilesSelect = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles = [];
    const newCaptions = [];

    for (const file of fileArray) {
      const isVideo = file.type.startsWith('video/');
      if (!file.type.startsWith('image/') && !isVideo) {
        setError('Some files were skipped - only images and videos are allowed');
        continue;
      }
      const maxSize = isVideo ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(isVideo ? 'Some videos were skipped - max 100MB each' : 'Some images were skipped - max 50MB each');
        continue;
      }
      validFiles.push(file);
      newCaptions.push('');
    }

    if (validFiles.length === 0) return;

    setError('');

    // Load previews in order
    const newPreviews = [];
    for (const file of validFiles) {
      if (file.type.startsWith('video/')) {
        newPreviews.push('video');
      } else {
        const preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        newPreviews.push(preview);
      }
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setCaptions(prev => [...prev, ...newCaptions]);
    setPreviews(prev => [...prev, ...newPreviews]);
  };
  const handleInputChange = (e) => {
    handleFilesSelect(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const updateCaption = (index, value) => {
    setCaptions(prev => prev.map((c, i) => i === index ? value : c));
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setCaptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      setError('');

      for (let i = 0; i < selectedFiles.length; i++) {
        setUploadProgress(`Uploading ${i + 1} of ${selectedFiles.length}...`);

        const formData = new FormData();
        formData.append('file', selectedFiles[i]);
        if (captions[i]) formData.append('caption', captions[i]);
        if (selectedAlbum) formData.append('albumId', selectedAlbum);

        const response = await photoService.uploadPhoto(formData);

        if (response.success) {
          onUploaded(response.data);
        } else {
          setError(`Failed to upload ${selectedFiles[i].name}`);
        }
      }

      onClose();
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content upload-modal" onClick={e => e.stopPropagation()}>
        <div className="upload-header">
          <h2>Upload Photos</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {error && <div className="upload-error">{error}</div>}

        {/* Drop zone - always visible to add more */}
        <div
          className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${selectedFiles.length > 0 ? 'compact' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <FiImage className="dropzone-icon" />
          <p className="dropzone-text">
            {selectedFiles.length > 0
              ? 'Click or drag to add more photos'
              : 'Drag and drop photos here, or click to browse'}
          </p>
          {selectedFiles.length === 0 && (
            <p className="dropzone-hint">Supports: JPG, PNG, GIF (max 50MB each)</p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/quicktime"
            multiple
            onChange={handleInputChange}
            hidden
          />
        </div>

        {/* Previews */}
        {previews.length > 0 && (
          <div className="upload-previews">
            {previews.map((preview, index) => (
              <div key={index} className="upload-preview-item">
                <div className="preview-thumb-container">
                  {preview === 'video' ? (
                    <div className="preview-thumb preview-video">▶</div>
                  ) : (
                    <img src={preview} alt={`Preview ${index + 1}`} className="preview-thumb" />
                  )}
                  <button className="preview-remove" onClick={() => removeFile(index)}>
                    <FiX />
                  </button>
                </div>
                <input
                  type="text"
                  className="form-input preview-caption-input"
                  placeholder="Caption (optional)"
                  value={captions[index] || ''}
                  onChange={(e) => updateCaption(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        )}

        {albums.length > 0 && (
          <div className="form-group" style={{ padding: '0 16px' }}>
            <label className="form-label">Add to Album (optional)</label>
            <select
              className="form-input"
              value={selectedAlbum}
              onChange={(e) => setSelectedAlbum(e.target.value)}
            >
              <option value="">No album</option>
              {albums.map(album => (
                <option key={album.albumID} value={album.albumID}>
                  {album.albumName}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="upload-actions">
          <span className="upload-count">
            {selectedFiles.length > 0 && `${selectedFiles.length} photo${selectedFiles.length !== 1 ? 's' : ''} selected`}
          </span>
          <div className="upload-buttons">
            <button className="btn btn-ghost" onClick={onClose} disabled={uploading}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner spinner-sm"></span>
                  {uploadProgress}
                </>
              ) : (
                <>
                  <FiUpload /> Upload {selectedFiles.length > 1 ? `${selectedFiles.length} Photos` : 'Photo'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUpload;